import { organizationsId, type OrganizationsId } from "./Organizations";
import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for invitations */
export type InvitationsId = string & { __flavor: "InvitationsId" };

/**
 * Represents the table public.invitations
 * @uniqueConstraints {"invitations_pkey":{"name":"invitations_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"invitations_email_index":{"name":"invitations_email_index","isPrimary":false,"isUnique":false,"columns":[{"name":"email","definition":"email","predicate":null}]},"invitations_organization_id_index":{"name":"invitations_organization_id_index","isPrimary":false,"isUnique":false,"columns":[{"name":"organization_id","definition":"organization_id","predicate":null}]}}
 */
export default interface InvitationsTable {
  id: ColumnType<InvitationsId, InvitationsId, InvitationsId>;

  organizationId: ColumnType<OrganizationsId, OrganizationsId, OrganizationsId>;

  email: ColumnType<string, string, string>;

  role: ColumnType<string | null, string | null, string | null>;

  status: ColumnType<string, string | undefined, string>;

  expiresAt: ColumnType<Date, Date | string, Date | string>;

  inviterId: ColumnType<UsersId, UsersId, UsersId>;
}

export type Invitations = Selectable<InvitationsTable>;

export type NewInvitations = Insertable<InvitationsTable>;

export type InvitationsUpdate = Updateable<InvitationsTable>;

export const invitationsId = z.string() as unknown as z.Schema<InvitationsId>;

export const invitations = z.object({
  id: invitationsId,
  organizationId: organizationsId,
  email: z.string(),
  role: z.string().nullable(),
  status: z.string(),
  expiresAt: z.date(),
  inviterId: usersId,
});

export const invitationsInitializer = z.object({
  id: invitationsId,
  organizationId: organizationsId,
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string().optional(),
  expiresAt: z.date(),
  inviterId: usersId,
});

export const invitationsMutator = z.object({
  id: invitationsId.optional(),
  organizationId: organizationsId.optional(),
  email: z.string().optional(),
  role: z.string().optional().nullable(),
  status: z.string().optional(),
  expiresAt: z.date().optional(),
  inviterId: usersId.optional(),
});

// ── constraints for Invitations ─────────────────────────────────
export const InvitationsConstraints = {
  invitations_pkey: {
    name: "invitations_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  invitations_email_index: {
    name: "invitations_email_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "email", definition: "email", predicate: null }],
  },
  invitations_organization_id_index: {
    name: "invitations_organization_id_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "organization_id", definition: "organization_id", predicate: null }],
  },
} as const;
export type InvitationsConstraintsType = typeof InvitationsConstraints;
